import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import { RenderSecure } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

/**
 * Fixed Assets — uses journal entries with type=depreciation or appreciation
 * Assets are accounts of type=asset with sub_type=fixed_asset
 */
const FixedAssets = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "register"; // register | depreciate | appreciate

  const [assets,    setAssets]    = useState([]);
  const [accounts,  setAccounts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [fyList,    setFyList]    = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving,    setSaving]    = useState(false);

  // Create asset form (creates as GL account of type asset)
  const [assetForm, setAssetForm] = useState({
    account_code: "", name: "", description: "",
    purchase_date: new Date().toISOString().split("T")[0],
    cost: "", useful_life_years: "", depreciation_method: { value: "straight_line", label: "Straight Line" },
  });

  // Depreciation / appreciation form
  const [adjForm, setAdjForm] = useState({
    asset_id: null, amount: "", entry_date: new Date().toISOString().split("T")[0],
    accum_depr_account: null, description: "", project_id: null,
  });

  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchMeta();
  }, []);

  const fetchMeta = async () => {
    setLoading(true);
    const [fyRes, accRes, projRes] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.listAccounts({ postable_only: 1 }),
      ajaxAccounting.listProjects({ status: "active" }),
    ]);
    if (fyRes.status === "OK") {
      const list = fyRes.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
    if (accRes.status === "OK") {
      const all = accRes.details || [];
      setAccounts(all);
      // Fixed assets = asset accounts (excluding cash/bank)
      const fixedAssets = all.filter((a) =>
        a.type === "asset" &&
        !a.account_code?.startsWith("101") &&
        !a.account_code?.startsWith("102") &&
        !a.name?.toLowerCase().includes("cash") &&
        !a.name?.toLowerCase().includes("bank")
      );
      setAssets(fixedAssets);
    }
    if (projRes.status === "OK") setProjects(projRes.details || []);
    setLoading(false);
  };

  // Account type filter helpers
  const assetAccountOptions = accounts
    .filter((a) => a.type === "asset" && !a.account_code?.startsWith("101") && !a.account_code?.startsWith("102"))
    .map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));

  const accumDeprOptions = accounts
    .filter((a) => a.type === "asset" && (a.name?.toLowerCase().includes("accum") || a.name?.toLowerCase().includes("depreciation") || a.account_code?.startsWith("17")))
    .map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));

  const deprExpenseOptions = accounts
    .filter((a) => a.type === "expense" && (a.name?.toLowerCase().includes("depreciation") || a.account_code?.startsWith("60")))
    .map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));

  const equityOptions = accounts
    .filter((a) => a.type === "equity" || a.type === "revenue")
    .map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));

  const projectOptions = projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }));
  const fyOptions      = fyList.map((y) => ({ value: y.id, label: y.name }));

  // ── Create asset (as a GL account) ─────────────────────────────────────────
  const handleCreateAsset = async (e) => {
    e.preventDefault();
    if (!assetForm.account_code || !assetForm.name) { toast.error("Account code and name required"); return; }
    setSaving(true);
    const res = await ajaxAccounting.createAccount({
      account_code:        assetForm.account_code,
      name:                assetForm.name,
      type:                "asset",
      description:         assetForm.description,
      allow_direct_posting:1,
    });
    if (res.status === "OK") {
      toast.success("Asset account created!");
      fetchMeta();
      setShowModal(false);
      setAssetForm({ account_code:"",name:"",description:"",purchase_date:new Date().toISOString().split("T")[0],cost:"",useful_life_years:"",depreciation_method:{value:"straight_line",label:"Straight Line"} });
    } else toast.error(res.message);
    setSaving(false);
  };

  // ── Post depreciation journal entry ──────────────────────────────────────
  const handleDepreciation = async (e) => {
    e.preventDefault();
    if (!adjForm.asset_id || !adjForm.amount || !selectedFY) {
      toast.error("Asset, amount and financial year are required");
      return;
    }
    if (!adjForm.accum_depr_account) { toast.error("Select accumulated depreciation account"); return; }
    setSaving(true);

    // Depreciation: Dr Depreciation Expense / Cr Accumulated Depreciation
    const deprExpAcc = deprExpenseOptions[0]; // first depreciation expense account as default
    if (!deprExpAcc) { toast.error("No depreciation expense account found. Create one in Chart of Accounts."); setSaving(false); return; }

    const res = await ajaxAccounting.createAndPostJournal({
      type:              "depreciation",
      entry_date:        adjForm.entry_date,
      reference:         `DEPR-${adjForm.asset_id.label?.split("—")[0].trim()}`,
      description:       adjForm.description || `Depreciation — ${adjForm.asset_id.label}`,
      financial_year_id: selectedFY.value,
      project_id:        adjForm.project_id?.value || null,
      lines: [
        { account_id: deprExpAcc.value,               debit: parseFloat(adjForm.amount), credit: 0, description: `Depreciation — ${adjForm.asset_id.label}` },
        { account_id: adjForm.accum_depr_account.value, debit: 0, credit: parseFloat(adjForm.amount), description: `Accumulated depreciation — ${adjForm.asset_id.label}` },
      ],
    });
    if (res.status === "OK") {
      toast.success("Depreciation posted to ledger!");
      setAdjForm({ asset_id:null, amount:"", entry_date:new Date().toISOString().split("T")[0], accum_depr_account:null, description:"", project_id:null });
    } else toast.error(res.message);
    setSaving(false);
  };

  // ── Post appreciation journal entry ────────────────────────────────────────
  const handleAppreciation = async (e) => {
    e.preventDefault();
    if (!adjForm.asset_id || !adjForm.amount || !selectedFY) { toast.error("Asset, amount and financial year required"); return; }
    if (!adjForm.accum_depr_account) { toast.error("Select revaluation reserve account"); return; }
    setSaving(true);

    // Appreciation: Dr Asset Account / Cr Revaluation Reserve (equity)
    const res = await ajaxAccounting.createAndPostJournal({
      type:              "appreciation",
      entry_date:        adjForm.entry_date,
      reference:         `APPR-${adjForm.asset_id.label?.split("—")[0].trim()}`,
      description:       adjForm.description || `Appreciation — ${adjForm.asset_id.label}`,
      financial_year_id: selectedFY.value,
      project_id:        adjForm.project_id?.value || null,
      lines: [
        { account_id: adjForm.asset_id.value,           debit: parseFloat(adjForm.amount), credit: 0, description: `Asset appreciation — ${adjForm.asset_id.label}` },
        { account_id: adjForm.accum_depr_account.value,  debit: 0, credit: parseFloat(adjForm.amount), description: `Revaluation reserve — ${adjForm.asset_id.label}` },
      ],
    });
    if (res.status === "OK") {
      toast.success("Appreciation posted to ledger!");
      setAdjForm({ asset_id:null, amount:"", entry_date:new Date().toISOString().split("T")[0], accum_depr_account:null, description:"", project_id:null });
    } else toast.error(res.message);
    setSaving(false);
  };

  const columns = [
    { key:"account_code", label:"Code",    render:(v) => <code>{v}</code> },
    { key:"name",          label:"Asset",   render:(v) => <strong>{v}</strong> },
    { key:"description",   label:"Description", render:(v) => <span className="tx-12">{v || "—"}</span> },
    { key:"is_active",     label:"Status",  render:(v) => v !== 0 ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Disposed</span> },
    { key:"id",            label:"Actions", render:(id, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/ledger?account_id=${id}`)} title="View Ledger">
          <i className="fa-solid fa-table-list"></i>
        </button>
        <RenderSecure code="ACC-ASSETS-DEPRECIATE">
          <button className="btn btn-sm btn-outline-warning" title="Depreciate"
            onClick={() => { setAdjForm((f) => ({ ...f, asset_id: { value: id, label: `${row.account_code} — ${row.name}` } })); navigate("/accounting/assets?mode=depreciate"); }}>
            <i className="fa-solid fa-arrow-trend-down"></i>
          </button>
        </RenderSecure>
        <RenderSecure code="ACC-ASSETS-APPRECIATE">
          <button className="btn btn-sm btn-outline-success" title="Appreciate"
            onClick={() => { setAdjForm((f) => ({ ...f, asset_id: { value: id, label: `${row.account_code} — ${row.name}` } })); navigate("/accounting/assets?mode=appreciate"); }}>
            <i className="fa-solid fa-arrow-trend-up"></i>
          </button>
        </RenderSecure>
      </div>
    )},
  ];

  const deprMethod = [
    { value:"straight_line",      label:"Straight Line" },
    { value:"declining_balance",  label:"Declining Balance" },
    { value:"units_of_production",label:"Units of Production" },
  ];

  const pageTitle = mode === "depreciate" ? "Depreciate Asset" : mode === "appreciate" ? "Appreciate Asset" : "Asset Register";
  const pageTabs  = [
    { key:"register",   label:"Asset Register",   icon:"fa-building" },
    { key:"depreciate", label:"Depreciate",        icon:"fa-arrow-trend-down" },
    { key:"appreciate", label:"Appreciate",        icon:"fa-arrow-trend-up" },
  ];

  return (
    <AppContainer
      title={pageTitle}
      previousPageTitle="Assets & Liabilities"
      description="Fixed assets are tracked as GL accounts. Use Depreciate to post depreciation entries (Dr Depreciation Expense / Cr Accumulated Depreciation). Use Appreciate to revalue an asset upwards (Dr Asset / Cr Revaluation Reserve)."
      actions={[
        { label:"Add Asset",    icon:"fa-solid fa-plus",             className:"btn btn-primary",         onClick:() => setShowModal(true) },
        { label:"View Ledger",  icon:"fa-solid fa-table-list",       className:"btn btn-outline-secondary",onClick:() => navigate("/accounting/ledger") },
      ]}
    >
      <Toaster />

      {/* FY selector for journal entries */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {pageTabs.map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${mode === t.key ? "active" : ""}`}
              onClick={() => navigate(`/accounting/assets?mode=${t.key}`)}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* REGISTER TAB */}
      {mode === "register" && (
        <div className="card custom-card p-3">
          <ExportBar data={assets}
            columns={[{key:"account_code",label:"Code"},{key:"name",label:"Asset"},{key:"description",label:"Description"}]}
            filename="fixed-assets" pdfTitle="Fixed Asset Register"
            printCode="ACC-ASSETS" exportCode="ACC-ASSETS" />
          <DataTable columns={columns} data={assets} loading={loading} pageSize={15}
            emptyMessage="No fixed asset accounts found. Add assets to the Chart of Accounts with type 'asset'." />
        </div>
      )}

      {/* DEPRECIATE TAB */}
      {mode === "depreciate" && (
        <div className="row">
          <div className="col-lg-7">
            <div className="card custom-card p-4">
              <h6 className="fw-semibold mb-3">Post Depreciation Entry</h6>
              <div className="alert alert-info tx-12 mb-3">
                <strong>Depreciation entry:</strong><br/>
                Dr Depreciation Expense &nbsp;&rarr;&nbsp; Cr Accumulated Depreciation
              </div>
              <form onSubmit={handleDepreciation}>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Asset Account <span className="tx-danger">*</span></label>
                    <Select options={assetAccountOptions} value={adjForm.asset_id} onChange={(v) => setAdjForm({...adjForm,asset_id:v})} placeholder="Select asset account" isSearchable />
                  </div>
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Accumulated Depreciation Account <span className="tx-danger">*</span></label>
                    <Select options={accumDeprOptions.length ? accumDeprOptions : accounts.filter((a) => a.type === "asset").map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }))}
                      value={adjForm.accum_depr_account} onChange={(v) => setAdjForm({...adjForm,accum_depr_account:v})}
                      placeholder="Select accumulated depreciation account" isSearchable />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label tx-12 fw-semibold">Depreciation Amount (UGX) <span className="tx-danger">*</span></label>
                    <input type="number" className="form-control" min="0" step="0.01" value={adjForm.amount}
                      onChange={(e) => setAdjForm({...adjForm,amount:e.target.value})} required />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label tx-12 fw-semibold">Entry Date</label>
                    <input type="date" className="form-control" value={adjForm.entry_date}
                      onChange={(e) => setAdjForm({...adjForm,entry_date:e.target.value})} />
                  </div>
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Project / Cost Centre</label>
                    <Select options={projectOptions} value={adjForm.project_id} onChange={(v) => setAdjForm({...adjForm,project_id:v})} placeholder="Link to project (optional)" isClearable isSearchable />
                  </div>
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Description</label>
                    <input className="form-control" value={adjForm.description}
                      onChange={(e) => setAdjForm({...adjForm,description:e.target.value})}
                      placeholder="e.g. Annual depreciation — Motor Vehicle" />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-4">
                  <button type="submit" className="btn btn-warning" disabled={saving}>
                    <i className="fa-solid fa-arrow-trend-down me-1"></i>
                    {saving ? "Posting..." : "Post Depreciation"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card custom-card p-3">
              <h6 className="fw-semibold mb-2">Depreciation Methods</h6>
              <div className="tx-12 tx-muted">
                <p><strong>Straight Line:</strong> Equal amount each year.<br/><code>Cost ÷ Useful Life</code></p>
                <p><strong>Declining Balance:</strong> Fixed percentage of book value.<br/><code>Book Value × Rate</code></p>
                <p className="mb-0"><strong>Units of Production:</strong> Based on actual usage.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPRECIATE TAB */}
      {mode === "appreciate" && (
        <div className="row">
          <div className="col-lg-7">
            <div className="card custom-card p-4">
              <h6 className="fw-semibold mb-3">Post Asset Appreciation Entry</h6>
              <div className="alert alert-info tx-12 mb-3">
                <strong>Appreciation entry:</strong><br/>
                Dr Asset Account &nbsp;&rarr;&nbsp; Cr Revaluation Reserve (Equity)
              </div>
              <form onSubmit={handleAppreciation}>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Asset Account <span className="tx-danger">*</span></label>
                    <Select options={assetAccountOptions} value={adjForm.asset_id} onChange={(v) => setAdjForm({...adjForm,asset_id:v})} placeholder="Select asset account" isSearchable />
                  </div>
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Revaluation Reserve / Equity Account <span className="tx-danger">*</span></label>
                    <Select options={equityOptions} value={adjForm.accum_depr_account} onChange={(v) => setAdjForm({...adjForm,accum_depr_account:v})}
                      placeholder="Select revaluation reserve or equity account" isSearchable />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label tx-12 fw-semibold">Appreciation Amount (UGX) <span className="tx-danger">*</span></label>
                    <input type="number" className="form-control" min="0" step="0.01" value={adjForm.amount}
                      onChange={(e) => setAdjForm({...adjForm,amount:e.target.value})} required />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label tx-12 fw-semibold">Entry Date</label>
                    <input type="date" className="form-control" value={adjForm.entry_date}
                      onChange={(e) => setAdjForm({...adjForm,entry_date:e.target.value})} />
                  </div>
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Project / Cost Centre</label>
                    <Select options={projectOptions} value={adjForm.project_id} onChange={(v) => setAdjForm({...adjForm,project_id:v})} placeholder="Link to project (optional)" isClearable isSearchable />
                  </div>
                  <div className="col-12">
                    <label className="form-label tx-12 fw-semibold">Description</label>
                    <input className="form-control" value={adjForm.description}
                      onChange={(e) => setAdjForm({...adjForm,description:e.target.value})}
                      placeholder="e.g. Land revaluation — Nakasero plot" />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-4">
                  <button type="submit" className="btn btn-success" disabled={saving}>
                    <i className="fa-solid fa-arrow-trend-up me-1"></i>
                    {saving ? "Posting..." : "Post Appreciation"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card custom-card p-3">
              <h6 className="fw-semibold mb-2">When to Appreciate</h6>
              <div className="tx-12 tx-muted">
                <p>Use appreciation when an asset's market value has increased — e.g. land, buildings, or investment property revaluation.</p>
                <p className="mb-0">The increase goes to <strong>Revaluation Reserve</strong> in equity (not to the income statement unless the asset is sold).</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE ASSET MODAL */}
      {showModal && (
        <AppModal
          title="Add Fixed Asset"
          size="lg"
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateAsset}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Add Asset"}</button>
            </>
          }
        >
          <div className="alert alert-info tx-12">
            Assets are tracked as GL accounts. The account code should follow your numbering convention (e.g. 1500 series for non-current assets).
          </div>
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Account Code <span className="tx-danger">*</span></label>
              <input className="form-control" value={assetForm.account_code}
                onChange={(e) => setAssetForm({...assetForm,account_code:e.target.value})} placeholder="e.g. 1510" required />
            </div>
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Asset Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={assetForm.name}
                onChange={(e) => setAssetForm({...assetForm,name:e.target.value})} placeholder="e.g. Motor Vehicle — Landcruiser" required />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={assetForm.description}
                onChange={(e) => setAssetForm({...assetForm,description:e.target.value})}
                placeholder="Serial number, location, other details" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Depreciation Method</label>
              <Select options={deprMethod} value={assetForm.depreciation_method} onChange={(v) => setAssetForm({...assetForm,depreciation_method:v})} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Useful Life (years)</label>
              <input type="number" className="form-control" min="1" value={assetForm.useful_life_years}
                onChange={(e) => setAssetForm({...assetForm,useful_life_years:e.target.value})} placeholder="e.g. 5" />
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default FixedAssets;
